/**
 * useFullAuth Hook
 * Returns the user only if they have both Firebase auth AND a MongoDB profile
 */

import { useState, useEffect } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import { createAuthenticatedAxios } from '@/shared/services/axiosInstance';

const useFullAuth = () => {
    const {
        user: firebaseUser,
        loading: firebaseLoading,
        hasProfile,
        profileVersion,
    } = useAuth();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchUserProfile = async () => {
            if (!firebaseUser || !firebaseUser.emailVerified) {
                setUser(null);
                setLoading(false);
                return;
            }

            try {
                const api = createAuthenticatedAxios(firebaseUser);
                const response = await api.get('/api/users/me');
                setUser(response.data);
                setError(null);
            } catch (err) {
                if (err.response?.status === 404) {
                    // User exists in Firebase but not in MongoDB
                    setUser(null);
                } else {
                    setError(err);
                }
            } finally {
                setLoading(false);
            }
        };

        if (!firebaseLoading) {
            fetchUserProfile();
        }
    }, [firebaseUser, firebaseLoading, hasProfile, profileVersion]);

    return { user, loading: firebaseLoading || loading, error };
};

export default useFullAuth;
